<template>
  <div ref="switcherRef" class="lang-switcher">
    <button
      type="button"
      class="lang-trigger"
      :class="{ 'is-open': open }"
      :style="{ color: titleColor }"
      :aria-expanded="open"
      aria-haspopup="listbox"
      aria-label="Language"
      @click="toggle"
    >
      <svg
        class="lang-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c2.5 2.6 3.8 5.7 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.7-3.8-9S9.5 5.6 12 3z" />
      </svg>
      <span class="lang-current">{{ currentName }}</span>
      <svg
        class="lang-chevron"
        :class="{ 'is-open': open }"
        viewBox="0 0 10 6"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        aria-hidden="true"
      >
        <path d="M1 1l4 4 4-4" />
      </svg>
    </button>
    <transition name="lang-menu">
      <ul v-show="open" class="lang-menu" role="listbox">
        <li
          v-for="item in localeOptions"
          :key="item.code"
          class="lang-option"
          :class="{ active: item.code === locale }"
          role="option"
          :aria-selected="item.code === locale"
          @click="selectLang(item.code)"
        >
          <span class="lang-option-text">{{ item.name }}</span>
          <svg
            v-if="item.code === locale"
            class="lang-check"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M2 6.5L4.8 9.2 10 3.5" />
          </svg>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useAppStore } from '~/store/app'

const { locale, locales } = useI18n()
const switchLocalePath = useSwitchLocalePath()
const appStore = useAppStore()

/* 与主导航菜单同源的文字颜色：Header 在深色 banner（白字）与
   滚动后白底（黑字）两态间切换时传入，避免语言选择器文字与背景融合不可见 */
defineProps({
  titleColor: {
    type: String,
    default: '',
  },
})

const open = ref(false)
const switcherRef = ref<HTMLElement | null>(null)

const localeOptions = computed(() =>
  locales.value.map((item) => {
    if (typeof item === 'string') {
      return {
        code: item,
        name: item,
      }
    }

    return {
      code: item.code,
      name: item.name || item.code,
    }
  })
)

const currentName = computed(
  () => localeOptions.value.find((item) => item.code === locale.value)?.name || locale.value
)

const toggle = () => {
  open.value = !open.value
}

const selectLang = async (code: string) => {
  open.value = false

  if (!code || code === locale.value) return

  appStore.setLocale(code)

  const localizedPath = switchLocalePath(code)
  await navigateTo(localizedPath)
}

const handleClickOutside = (event: MouseEvent) => {
  if (switcherRef.value && !switcherRef.value.contains(event.target as Node)) {
    open.value = false
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    open.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleKeydown)
})

watch(
  locale,
  (value) => {
    if (value && value !== appStore.locale) {
      appStore.setLocale(value)
    }
  },
  { immediate: true }
)
</script>

<style lang="less" scoped>
@brandBlue: #00a0e9;

.lang-switcher {
  position: relative;
  display: inline-flex;
}

/* 触发按钮：胶囊形，中性半透明描边与底色，
   在白底（黑字）与深色 banner（白字）两态 header 下均清晰可见 */
.lang-trigger {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 9px;
  border: 1px solid rgba(128, 128, 128, 0.35);
  border-radius: 13px;
  background: rgba(128, 128, 128, 0.1);
  color: inherit;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.3s ease, background-color 0.3s ease, color 0.3s ease;

  &:hover,
  &.is-open {
    border-color: @brandBlue;
    background: rgba(0, 160, 233, 0.08);
  }

  &:focus-visible {
    outline: 2px solid rgba(0, 160, 233, 0.5);
    outline-offset: 2px;
  }
}
.lang-icon {
  width: 14px;
  height: 14px;
  flex: none;
  opacity: 0.85;
}
.lang-current {
  white-space: nowrap;
}
.lang-chevron {
  width: 9px;
  height: 6px;
  flex: none;
  opacity: 0.7;
  transition: transform 0.3s ease;

  &.is-open {
    transform: rotate(180deg);
  }
}

/* 下拉面板：白底浮层，与菜单弹层风格一致 */
.lang-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  min-width: 96px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.06);
}
.lang-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  color: #333;
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.25s ease, color 0.25s ease;

  &:hover {
    background: rgba(0, 160, 233, 0.08);
    color: @brandBlue;
  }

  &.active {
    color: @brandBlue;
    font-weight: 600;
    background: rgba(0, 160, 233, 0.06);
  }
}
.lang-check {
  width: 11px;
  height: 11px;
  flex: none;
}

/* 面板展开/收起过渡 */
.lang-menu-enter-active,
.lang-menu-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.lang-menu-enter-from,
.lang-menu-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (max-width: 768px) {
  .lang-trigger {
    height: 24px;
    font-size: 12px;
  }
  .lang-menu {
    min-width: 88px;
  }
  .lang-option {
    padding: 8px 10px;
  }
}
</style>
